/*
 * GirgitHost — decides where Girgit shows up.
 * Rules:
 *   - Never on the client portal, login pages, careers public pages, home, or Google-auth callback.
 *   - Only shows for admin / employee sessions (admin, hr, accountant, employee roles share this shell).
 *   - Reads user id from admin_user or employee_user localStorage entry for per-user persistence.
 *
 * Also bridges sonner success toasts into Girgit's event bus so he reacts to real app actions
 * (task created / completed / updated / payment recorded / nomination submitted, etc.) with a
 * party-hat cheer + confetti burst.  Uses a MutationObserver on the sonner container so no
 * existing call sites need to change.
 */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Girgit from './Girgit';
import { getAdminUser, isAdminAuthenticated } from '../../lib/adminAuth';
import { getEmployeeUser, isEmployeeAuthenticated } from '../../lib/employeeAuth';

const BLOCKED_PREFIXES = [
  '/client',              // client portal
  '/careers',             // public careers pages
  '/career',              // career login/dashboard
  '/admin/login',
  '/employee/login',
  '/auth',
  '/employee/auth/callback',
];

// Words in success toasts that make Girgit celebrate.
const CHEER_KEYWORDS = [
  'created', 'completed', 'done', 'saved', 'added', 'sent', 'submitted', 'approved',
  'published', 'uploaded', 'assigned', 'moved to done', 'recorded', 'paid', 'winner',
  'nominated', 'nomination', 'closed', 'resolved', 'imported', 'downloaded', 'success',
];
// Words that make him look worried (soft empathy, no confetti).
const WORRY_KEYWORDS = [
  'failed', 'error', 'unable', 'denied', 'rejected', 'invalid', 'missing',
];

function pickMood(text) {
  const t = (text || '').toLowerCase();
  if (WORRY_KEYWORDS.some(k => t.includes(k))) return 'worry';
  if (CHEER_KEYWORDS.some(k => t.includes(k))) return 'cheer';
  return null;
}

function useSonnerReactions() {
  useEffect(() => {
    // Track already-seen toasts so we don't fire twice.
    const seen = new WeakSet();
    const process = (root) => {
      // Sonner renders each toast as `[data-sonner-toast]`
      const toasts = root.querySelectorAll('[data-sonner-toast]');
      toasts.forEach((el) => {
        if (seen.has(el)) return;
        seen.add(el);
        const type = el.getAttribute('data-type') || '';
        // Prefer success/error signals, fall back to text keywords
        const text = el.textContent || '';
        let mood = null;
        if (type === 'success') mood = pickMood(text) || 'cheer';
        else if (type === 'error') mood = 'worry';
        else mood = pickMood(text);
        if (mood === 'cheer') window.dispatchEvent(new CustomEvent('girgit:cheer'));
        else if (mood === 'worry') window.dispatchEvent(new CustomEvent('girgit:worry'));
      });
    };
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches?.('[data-sonner-toast]')) {
            const el = n;
            const type = el.getAttribute('data-type') || '';
            const text = el.textContent || '';
            let mood = type === 'success' ? (pickMood(text) || 'cheer')
                    : type === 'error' ? 'worry'
                    : pickMood(text);
            if (mood === 'cheer') window.dispatchEvent(new CustomEvent('girgit:cheer'));
            else if (mood === 'worry') window.dispatchEvent(new CustomEvent('girgit:worry'));
          } else if (n.querySelectorAll) {
            process(n);
          }
        });
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
    // Initial sweep in case toasts existed before we mounted.
    process(document.body);
    return () => obs.disconnect();
  }, []);
}

export default function GirgitHost() {
  const location = useLocation();
  const path = location.pathname;
  useSonnerReactions();

  // Any Google-auth hash callback → hide (auth transitions get noisy)
  if ((location.hash || '').includes('session_id=')) return null;

  // Homepage & any blocked prefix → hide
  if (path === '/' ) return null;
  if (BLOCKED_PREFIXES.some(prefix => path === prefix || path.startsWith(prefix + '/'))) return null;

  // Only show for authenticated admin or employee sessions
  const admin = isAdminAuthenticated() ? getAdminUser() : null;
  const employee = isEmployeeAuthenticated() ? getEmployeeUser() : null;
  const user = admin || employee;
  if (!user) return null;

  const uid = user.id || user._id || user.email || 'anon';
  const role = (admin ? (admin.role || 'admin') : (employee?.role || 'employee')).toLowerCase();

  return <Girgit userId={uid} role={role} />;
}
